/* ==========================================================================
   VORAQEE — brand behaviour
   --------------------------------------------------------------------------
   A separate file from the vendor's theme.js, deliberately. The theme is
   licensed from DropStudios and may be updated; keeping every VORAQEE change
   in files of our own means a vendor update can be dropped in without
   hand-merging. Nothing of ours lives inside theme.js.

   Dependency-free, same as the theme. The theme namespaces its custom elements
   `ds-`; ours use `vq-` and `data-vq-*` so the two are never confused.

   Store data and interface copy come from `window.VORAQEE`, written by
   snippets/voraqee-config.liquid — no English is hard-coded here.

   Behaviours bind by selector and re-bind on `shopify:section:load`, because
   this theme re-renders whole sections through the Section Rendering API (cart
   drawer, recommendations) and a one-shot querySelectorAll would silently stop
   working the moment that happens.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__voraqeeInit) return;
  window.__voraqeeInit = true;

  var BOUND = 'data-vq-bound';
  var behaviours = [];
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function config() {
    return window.VORAQEE || {};
  }

  function says(key, fallback) {
    var strings = config().strings || {};
    return strings[key] || fallback;
  }

  function register(name, selector, setup) {
    behaviours.push({ name: name, selector: selector, setup: setup });
  }

  function run(root) {
    var scope = root && root.querySelectorAll ? root : document;

    behaviours.forEach(function (b) {
      var matches = Array.prototype.slice.call(scope.querySelectorAll(b.selector));
      /* querySelectorAll only looks downward, so a re-rendered section that is
         itself the match would be missed. */
      if (scope.matches && scope.matches(b.selector)) matches.push(scope);

      matches.forEach(function (el) {
        var bound = (el.getAttribute(BOUND) || '').split(' ').filter(Boolean);
        if (bound.indexOf(b.name) !== -1) return;
        bound.push(b.name);
        el.setAttribute(BOUND, bound.join(' '));
        try {
          b.setup(el);
        } catch (e) {
          /* One broken behaviour must not stop the others binding. */
        }
      });
    });
  }

  function escapeHtml(value) {
    return String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
  }

  /* ======================================================================== */
  /* Toast                                                                    */
  /* ======================================================================== */

  /* One host, created once and reused, so every confirmation announces through
     a single live region. A fresh region per message is read unpredictably by
     screen readers, and several at once are read in an order nobody controls. */

  var VISIBLE_MS = 2600;
  var EXIT_MS = 320;
  var toastHost = null;

  function ensureToastHost() {
    if (toastHost && document.body && document.body.contains(toastHost)) return toastHost;
    if (!document.body) return null;

    toastHost = document.createElement('div');
    toastHost.className = 'vq-toast-host';
    toastHost.setAttribute('role', 'status');
    toastHost.setAttribute('aria-live', 'polite');
    document.body.appendChild(toastHost);
    return toastHost;
  }

  function toast(message) {
    if (!message) return;
    var host = ensureToastHost();
    if (!host) return;

    var el = document.createElement('div');
    el.className = 'vq-toast';
    el.textContent = message;
    host.appendChild(el);

    /* Let the node land before the entry transition is armed, or it animates
       from its finished state and simply appears. */
    requestAnimationFrame(function () {
      el.setAttribute('data-vq-in', '');
    });

    window.setTimeout(function () {
      el.removeAttribute('data-vq-in');
      /* A timer, not transitionend: under reduced motion the transition is
         removed and that event would never fire, orphaning the node. */
      window.setTimeout(function () {
        if (el.parentNode) el.parentNode.removeChild(el);
      }, EXIT_MS);
    }, VISIBLE_MS);
  }

  window.voraqeeToast = toast;

  /* ======================================================================== */
  /* Local price display                                                      */
  /* ======================================================================== */

  /* Shows prices in the visitor's own currency while browsing. The store still
     charges its base currency — presenting AND charging a local currency is
     Shopify Payments territory, unavailable to an Indian entity — so this is
     display only, and honest about it: the charged amount stays beside the
     converted one.

     Amounts are read from [data-vq-amount], written by snippets/voraqee-money.
     Parsing the rendered string instead would have to cope with per-locale
     separators and an optional currency code, which is how converters end up
     off by a factor of a hundred.

     Fails closed: no rates, a stale cache or an unknown currency and the page
     keeps the server-rendered base price. A wrong price is far worse than an
     unconverted one. */

  var FX_CACHE = 'voraqee_fx_rates';
  var FX_CHOICE = 'voraqee_currency';
  var FX_COUNTRY = 'voraqee_country';
  var FX_TTL = 12 * 60 * 60 * 1000;
  var FX_ENDPOINT = 'https://open.er-api.com/v6/latest/';

  /* Country to currency. Carried here because Shopify will not supply it:
     without Shopify Payments there are no per-market currencies, so
     localization.country.currency.iso_code reports the SHOP currency for every
     market — ask it about India and it answers the base. A country missing
     here simply shows base prices, which is the correct fallback. */
  var COUNTRY_CURRENCY = {
    IN: 'INR', US: 'USD', GB: 'GBP', CA: 'CAD', AU: 'AUD', NZ: 'NZD',
    AE: 'AED', SG: 'SGD', HK: 'HKD', JP: 'JPY', KR: 'KRW', MY: 'MYR',
    IL: 'ILS', CH: 'CHF', CZ: 'CZK', DK: 'DKK', NO: 'NOK', SE: 'SEK',
    PL: 'PLN', AT: 'EUR', BE: 'EUR', DE: 'EUR', ES: 'EUR', FI: 'EUR',
    FR: 'EUR', IE: 'EUR', IT: 'EUR', NL: 'EUR', PT: 'EUR',
  };

  var fx = { base: null, target: null, rate: null };

  function stored(key) {
    try {
      return localStorage.getItem(key) || null;
    } catch (e) {
      return null;
    }
  }

  function currencyForCountry(code) {
    if (!code) return null;
    var overrides = (config().money || {}).countryCurrencies || {};
    return overrides[code] || COUNTRY_CURRENCY[code] || null;
  }

  function targetCurrency() {
    var money = config().money || {};
    var base = money.baseCurrency || null;

    /* A stored value equal to base is not a real choice — it is the residue of
       Shopify's always-base market currency. Ignore it rather than pinning
       everyone to the base forever. */
    var chosen = stored(FX_CHOICE);
    if (chosen && chosen !== base) return chosen;

    if (money.countryCurrency && money.countryCurrency !== base) return money.countryCurrency;

    return currencyForCountry(stored(FX_COUNTRY)) || currencyForCountry(money.countryCode) || base;
  }

  function readRateCache(base) {
    try {
      var raw = JSON.parse(localStorage.getItem(FX_CACHE) || 'null');
      if (!raw || raw.base !== base || !raw.rates) return null;
      if (Date.now() - raw.at > FX_TTL) return null;
      return raw.rates;
    } catch (e) {
      return null;
    }
  }

  function fetchRates(base) {
    var cached = readRateCache(base);
    if (cached) return Promise.resolve(cached);

    return fetch(FX_ENDPOINT + encodeURIComponent(base), { mode: 'cors' })
      .then(function (r) {
        if (!r.ok) throw new Error('rates unavailable');
        return r.json();
      })
      .then(function (data) {
        if (!data || data.result !== 'success' || !data.rates) throw new Error('rates malformed');
        try {
          localStorage.setItem(FX_CACHE, JSON.stringify({ base: base, rates: data.rates, at: Date.now() }));
        } catch (e) {}
        return data.rates;
      });
  }

  function decimalsFor(currency) {
    try {
      return new Intl.NumberFormat('en', { style: 'currency', currency: currency }).resolvedOptions()
        .maximumFractionDigits;
    } catch (e) {
      return 2;
    }
  }

  /* Snaps a converted amount to a price ending in 99. A live rate produces
     8,576.90 — arithmetically right and commercially wrong.

     Which form of 99 is decided by the CURRENCY, not the individual price.
     Choosing per value was tried and split one catalogue across two formats:
     AED 329, 249, 219 and then 179.99. Deciding from the rate keeps every
     price in a currency consistent with its neighbours. */
  function charmRound(value, currency, rate) {
    if (!isFinite(value) || value <= 0) return value;

    var wholeNumbers = decimalsFor(currency) === 0 || (rate && rate >= 10);

    if (wholeNumbers) {
      if (value >= 1000) return Math.round(value / 100) * 100 - 1;
      return Math.max(99, Math.round(value / 10) * 10 - 1);
    }

    return Math.max(0.99, Math.round(value) - 0.01);
  }

  function formatLocal(majorUnits, currency, locale) {
    try {
      var amount = charmRound(majorUnits, currency, fx.rate);
      /* A snapped whole number should read as one; the currency default would
         render 8599 as "₹8,599.00" and undo the point of snapping it. */
      var whole = Math.abs(amount - Math.round(amount)) < 0.0001;

      return new Intl.NumberFormat(locale || undefined, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2,
      }).format(amount);
    } catch (e) {
      return null;
    }
  }

  function paintPrice(node) {
    var raw = node.getAttribute('data-vq-amount');
    if (raw === null || raw === '') return;

    var subunits = parseInt(raw, 10);
    if (isNaN(subunits)) return;

    /* Stash the server-rendered markup once, so a later currency change
       re-renders from the original rather than from a converted string. */
    if (!node.hasAttribute('data-vq-base-html')) {
      node.setAttribute('data-vq-base-html', node.innerHTML);
    }
    var baseHtml = node.getAttribute('data-vq-base-html');

    if (!fx.rate || fx.target === fx.base) {
      node.innerHTML = baseHtml;
      node.removeAttribute('data-vq-converted');
      return;
    }

    var local = formatLocal((subunits / 100) * fx.rate, fx.target, (config().money || {}).locale);
    if (!local) {
      node.innerHTML = baseHtml;
      return;
    }

    /* The charged amount stays visible. The shopper is billed the base figure,
       and UK/EU price-display rules expect the real one to be shown rather
       than buried. */
    node.innerHTML =
      '<span class="vq-money__local">' +
      escapeHtml(local) +
      '</span><span class="vq-money__base">' +
      baseHtml +
      '</span>';

    node.setAttribute('data-vq-converted', fx.target);
  }

  function paintAllPrices(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.vq-money[data-vq-amount]').forEach(paintPrice);
    /* querySelectorAll never returns the scope itself. */
    if (scope.matches && scope.matches('.vq-money[data-vq-amount]')) paintPrice(scope);
  }

  function applyFx() {
    var money = config().money || {};
    fx.base = money.baseCurrency || null;
    fx.target = targetCurrency();

    if (!fx.base || !fx.target) return Promise.resolve();

    /* No network call for the majority case. */
    if (fx.target === fx.base) {
      fx.rate = null;
      paintAllPrices(document);
      return Promise.resolve();
    }

    return fetchRates(fx.base)
      .then(function (rates) {
        var rate = rates[fx.target];
        if (typeof rate !== 'number' || !isFinite(rate) || rate <= 0) throw new Error('no rate');
        fx.rate = rate;
        paintAllPrices(document);
      })
      .catch(function () {
        fx.rate = null;
      });
  }

  window.voraqeeSetCountry = function (code) {
    try {
      if (code) localStorage.setItem(FX_COUNTRY, code);
      else localStorage.removeItem(FX_COUNTRY);
      localStorage.removeItem(FX_CHOICE);
    } catch (e) {}
    return applyFx();
  };

  window.voraqeeCurrencyFor = currencyForCountry;

  register('price', '.vq-money[data-vq-amount]', function (node) {
    /* Newly rendered prices (variant change, cart re-render) need painting
       with the rate already resolved. */
    if (fx.base) paintPrice(node);
  });

  /* The theme's variant picker re-fetches the section and swaps the price
     block in. That replacement markup carries fresh vq-money spans which have
     never been painted, so the converted figure would silently disappear on
     the first variant change without this. */
  document.addEventListener('variant:change', function () {
    window.setTimeout(function () {
      run(document);
      paintAllPrices(document);
    }, 60);
  });

  /* ======================================================================== */
  /* Wishlist                                                                 */
  /* ======================================================================== */

  /* Saving requires an account. The list is held in localStorage, which is
     per-device — a guest who saves on their phone would not find it on their
     laptop, and nothing ties it to them. Rather than let that fail silently
     later, a guest is sent to sign in the moment they first reach for it. */

  var WISH_KEY = 'voraqee_wishlist';

  /* Entries are objects carrying what the saved-items panel needs to draw a
     row. Resolving ids to products on open would mean a request each and an
     empty panel until they land. */
  function savedItems() {
    var raw;
    try {
      raw = JSON.parse(localStorage.getItem(WISH_KEY) || '[]');
    } catch (e) {
      return [];
    }
    if (!Array.isArray(raw)) return [];

    /* Earlier builds stored bare id strings. Upgrading someone's list is
       better than discarding it: a migrated row still links and still counts,
       and saving again from the product fills in the detail. */
    var out = [];
    for (var i = 0; i < raw.length; i++) {
      var entry = raw[i];
      if (typeof entry === 'string') entry = { id: entry };
      if (entry && entry.id) out.push(entry);
    }
    return out;
  }

  function savedIds() {
    return savedItems().map(function (entry) {
      return entry.id;
    });
  }

  function writeSaved(list) {
    try {
      localStorage.setItem(WISH_KEY, JSON.stringify(list));
    } catch (e) {}
  }

  function requireSignIn() {
    var customer = config().customer || {};
    if (customer.loggedIn) return false;
    if (!customer.loginUrl) return false;

    /* The login URL is not always a bare path — with new customer accounts
       Shopify hands over a redirect link that already carries a query string,
       so appending another "?" would produce a dead link. */
    var here = window.location.pathname + window.location.search;
    var join = customer.loginUrl.indexOf('?') === -1 ? '?' : '&';

    /* Both parameters, because the two account systems read different ones and
       a theme cannot tell from Liquid which is switched on.

       Classic accounts honour `return_to`. New customer accounts drop it
       silently and honour `return_url` instead — verified against this store,
       where `return_to` lands on the bare account page and the shopper loses
       the product they were saving. Sending both is safe: each system ignores
       the parameter it does not recognise. */
    window.location.href =
      customer.loginUrl +
      join +
      'return_url=' +
      encodeURIComponent(here) +
      '&return_to=' +
      encodeURIComponent(here);
    return true;
  }

  function paintSaveControls() {
    var list = savedIds();
    document.querySelectorAll('[data-vq-save]').forEach(function (button) {
      var id = button.getAttribute('data-product-id');
      var on = list.indexOf(id) !== -1;
      button.setAttribute('aria-pressed', on ? 'true' : 'false');

      /* The pill reads its own state, so the label has to change with it.
         aria-pressed alone would leave a sighted shopper looking at a button
         that says the same thing saved or not. */
      var label = button.querySelector('[data-vq-save-label]');
      var text = button.getAttribute(on ? 'data-vq-label-active' : 'data-vq-label');
      if (label && text) label.textContent = text;
    });

    document.querySelectorAll('[data-vq-wishlist-count]').forEach(function (el) {
      el.textContent = list.length;
      el.hidden = list.length === 0;
      el.classList.toggle('is-empty', list.length === 0);
    });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-vq-save]');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    /* Before anything is written, so nothing is half-saved against a session
       that does not exist yet. */
    if (requireSignIn()) return;

    var id = button.getAttribute('data-product-id');
    var list = savedItems();
    var active;

    if (savedIds().indexOf(id) !== -1) {
      list = list.filter(function (entry) {
        return entry.id !== id;
      });
      active = false;
    } else {
      var price = parseInt(button.getAttribute('data-product-price'), 10);
      list.push({
        id: id,
        title: button.getAttribute('data-product-title') || '',
        url: button.getAttribute('data-product-url') || '',
        image: button.getAttribute('data-product-image') || '',
        price: isNaN(price) ? null : price,
      });
      active = true;
    }

    writeSaved(list);
    paintSaveControls();
    renderWishlist();
    toast(active ? says('addedToWishlist', 'Added to wishlist') : says('removedFromWishlist', 'Removed from wishlist'));
  });

  register('save-control', '[data-vq-save]', function () {
    paintSaveControls();
  });

  /* ======================================================================== */
  /* Current region                                                           */
  /* ------------------------------------------------------------------------ */
  /* The drawer's region control names the region it is currently on. "Change
     region" on its own tells a shopper nothing about what it would be changing
     FROM — which is exactly what matters to the one whose region was guessed
     wrong.

     Names are read out of the country gate's own option list rather than kept
     in a second table here. Shopify decides which countries exist, so a copy
     would drift the moment a market is added or removed.                     */
  /* ======================================================================== */

  function regionName(code) {
    if (!code) return '';
    var option = document.querySelector(
      '[data-vq-select-panel] [role="option"][data-value="' + code + '"]'
    );
    if (!option) return code;
    var label = option.querySelector('span');
    return label ? label.textContent.trim() : code;
  }

  function paintRegionLabel() {
    var name = regionName(stored(FX_COUNTRY));
    document.querySelectorAll('[data-vq-region-current]').forEach(function (el) {
      el.textContent = name;
      el.hidden = !name;
    });
  }

  /* ======================================================================== */
  /* Video playback                                                           */
  /* ------------------------------------------------------------------------ */
  /* The markup already asks for autoplay, loop, muted and no controls, but
     the attributes are a request rather than a guarantee: a browser can
     refuse autoplay outright, and iOS in particular only honours it once the
     `muted` PROPERTY is set — the attribute alone is not enough on a video
     that has already been parsed.

     So each video is re-armed in script, and play is attempted again when it
     scrolls into view. A refused promise is swallowed: a still first frame is
     an acceptable outcome, an uncaught rejection in the console is not.      */
  /* ======================================================================== */

  function armVideo(video) {
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.controls = false;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    /* Stops the long-press menu offering download and playback controls. */
    video.setAttribute('disablepictureinpicture', '');
    video.setAttribute('controlslist', 'nodownload noplaybackrate noremoteplayback');
  }

  function tryPlay(video) {
    var attempt = video.play();
    if (attempt && attempt.catch) attempt.catch(function () {});
  }

  register('video-autoplay', 'video', function (video) {
    /* Card clips are driven by hover, so they must not join the videos that
       play from the moment the page loads and never stop. */
    if (video.classList.contains('showcase-card__video')) return;

    armVideo(video);
    tryPlay(video);

    /* It must never stop. A video can be paused by things the page never
       asked for — a browser reclaiming a background tab, a power-saving mode,
       an interrupted buffer — so anything that pauses it is answered by
       starting it again. */
    video.addEventListener('pause', function () {
      tryPlay(video);
    });

    video.addEventListener('ended', function () {
      /* `loop` normally makes this unreachable, but a stalled buffer can end
         a clip early on some browsers. */
      video.currentTime = 0;
      tryPlay(video);
    });

    /* Returning to the tab is the common case where playback was suspended
       without a pause event firing. */
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) tryPlay(video);
    });

    if (!window.IntersectionObserver) return;

    /* Scrolling back into view is a second chance to start, for browsers that
       refuse autoplay until the element is actually on screen. Nothing here
       pauses it — passing out of view is not a reason to stop. */
    new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) tryPlay(entry.target);
        });
      },
      { threshold: 0.01 }
    ).observe(video);
  });

  /* ======================================================================== */
  /* Help chat                                                                */
  /* ------------------------------------------------------------------------ */
  /* Greeting, then topics, then the questions inside a topic, then the answer
     — each printed as a message so it reads as a conversation.

     There is no text input, deliberately. Everything the shopper can say is
     something they tapped, because nothing here can answer a typed sentence.
     A box would invite one and then fail.

     Panel mechanics copy the saved-items drawer: `hidden` on the host, the
     scroll lock on the document element, Escape as a global listener that
     no-ops when already shut. One difference — focus returns to the launcher
     on close, where the saved-items panel strands it at the top of the page.  */
  /* ======================================================================== */

  var helpOpener = null;
  var helpTopics = null;
  var helpCopy = null;

  function helpSheet() {
    return document.getElementById('VoraqeeHelp');
  }

  function readHelpJson(sheet, selector, fallback) {
    var node = sheet.querySelector(selector);
    if (!node) return fallback;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return fallback;
    }
  }

  /* Blocks arrive flat and in order. A topic opens a group; every question
     after it joins that group until the next topic. Questions before any
     topic are dropped rather than filed under a heading they do not have. */
  function groupTopics(blocks) {
    var topics = [];
    var current = null;

    blocks.forEach(function (block) {
      if (block.type === 'category') {
        current = block.name ? { name: block.name, questions: [] } : null;
        if (current) topics.push(current);
        return;
      }

      if (block.type === 'question' && current && block.q) {
        current.questions.push({ q: block.q, a: block.a || '' });
      }
    });

    /* A topic with nothing in it is a dead end. */
    return topics.filter(function (topic) {
      return topic.questions.length > 0;
    });
  }

  function helpLog() {
    var sheet = helpSheet();
    return sheet ? sheet.querySelector('[data-vq-help-log]') : null;
  }

  function say(text, who, isHtml) {
    var log = helpLog();
    if (!log || !text) return;

    var msg = document.createElement('div');
    msg.className = 'vq-help__msg vq-help__msg--' + (who || 'bot');
    if (isHtml) msg.innerHTML = text;
    else msg.textContent = text;

    log.appendChild(msg);
    /* The newest message is the one they want to read. */
    log.scrollTop = log.scrollHeight;
  }

  /* A chip row is only answerable while it is the last thing said. Once a
     choice is taken the row is dropped, so the transcript above stays a
     record of the conversation rather than a field of stale buttons. */
  function clearChoices() {
    var log = helpLog();
    if (!log) return;

    var rows = log.querySelectorAll('[data-vq-help-replies]');
    Array.prototype.forEach.call(rows, function (row) {
      row.parentNode.removeChild(row);
    });
  }

  /* Choices live in the message stream, directly under the message they answer
     — not pinned to the foot of the panel. The thing to tap is then always
     where the eye already is. */
  function offer(choices) {
    var log = helpLog();
    if (!log) return;

    clearChoices();

    var row = document.createElement('div');
    row.className = 'vq-help__replies';
    row.setAttribute('data-vq-help-replies', '');

    choices.forEach(function (choice) {
      if (!choice.label) return;
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'vq-help__chip' + (choice.variant ? ' vq-help__chip--' + choice.variant : '');
      chip.textContent = choice.label;
      chip.addEventListener('click', function () {
        /* Clear first, so their reply lands below the chips rather than
           appearing above them for a frame. */
        clearChoices();
        choice.run(choice.label);
      });
      row.appendChild(chip);
    });

    log.appendChild(row);
    log.scrollTop = log.scrollHeight;
  }

  function showTopics(announce) {
    if (announce && helpCopy.prompt) say(helpCopy.prompt, 'bot');

    offer(
      helpTopics.map(function (topic) {
        return {
          label: topic.name,
          run: function (label) {
            say(label, 'you');
            showQuestions(topic);
          },
        };
      })
    );
  }

  function showQuestions(topic) {
    if (helpCopy.questionPrompt) say(helpCopy.questionPrompt, 'bot');

    var choices = topic.questions.map(function (item) {
      return {
        label: item.q,
        run: function (label) {
          say(label, 'you');
          say(item.a, 'bot', true);
          afterAnswer();
        },
      };
    });

    choices.push({
      label: helpCopy.missLabel,
      variant: 'quiet',
      run: function (label) {
        say(label, 'you');
        sayMiss();
      },
    });

    choices.push({
      label: helpCopy.backLabel,
      variant: 'quiet',
      run: function () {
        showTopics(false);
      },
    });

    offer(choices);
  }

  function afterAnswer() {
    offer([
      {
        label: helpCopy.backLabel,
        run: function () {
          showTopics(false);
        },
      },
      {
        label: helpCopy.missLabel,
        variant: 'quiet',
        run: function (label) {
          say(label, 'you');
          sayMiss();
        },
      },
    ]);
  }

  function sayMiss() {
    if (helpCopy.missReply) say(helpCopy.missReply, 'bot');

    var email = helpCopy.supportEmail;
    if (email) {
      /* A mailto, not a form: the panel collects nothing, so it should not
         look as though it does. The subject gives the message a handle when
         it lands. */
      var href = 'mailto:' + email + '?subject=' + encodeURIComponent('Question from the VORAQEE store');
      say('<a href="' + escapeHtml(href) + '">' + escapeHtml(email) + '</a>', 'bot', true);
    }

    offer([
      {
        label: helpCopy.restartLabel,
        run: function () {
          showTopics(true);
        },
      },
    ]);
  }

  function startHelp() {
    var log = helpLog();
    if (!log || log.childElementCount > 0) return; /* already talking */

    if (helpCopy.greeting) say(helpCopy.greeting, 'bot');

    if (!helpTopics.length) {
      /* No topics set up yet. Go straight to the one thing that still works
         rather than showing an empty panel. */
      sayMiss();
      return;
    }

    showTopics(true);
  }

  function openHelp(trigger) {
    var sheet = helpSheet();
    if (!sheet) return;

    if (helpTopics === null) {
      helpTopics = groupTopics(readHelpJson(sheet, '[data-vq-help-data]', []));
      helpCopy = readHelpJson(sheet, '[data-vq-help-copy]', {});
    }

    hideHello();

    helpOpener = trigger || null;
    sheet.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    if (helpOpener) helpOpener.setAttribute('aria-expanded', 'true');

    startHelp();

    var panel = sheet.querySelector('.vq-help__panel');
    if (panel) panel.focus();
  }

  function closeHelp() {
    var sheet = helpSheet();
    if (!sheet || sheet.hidden) return;

    sheet.hidden = true;
    document.documentElement.style.overflow = '';

    if (helpOpener) {
      helpOpener.setAttribute('aria-expanded', 'false');
      /* Back where they were, not at the top of the page. */
      helpOpener.focus();
      helpOpener = null;
    }
  }

  /* ------------------------------------------------------------------------ */
  /* The welcome wave                                                          */
  /* ------------------------------------------------------------------------ */
  /* Rendered only on the home page (decided in Liquid), and only once the
     country gate is out of the way — a bubble under a full-screen splash is a
     greeting nobody receives. The gate toggles `hidden` on .voraqee-gate, so
     that attribute is the signal.

     It waits, waves, and leaves. Nothing about it is sticky: there is no
     stored "already greeted" flag, because the ask was for it on every home
     page load. */

  var HELLO_WAIT = 1400; /* let the page settle first */
  var HELLO_LIFE = 9000; /* then it shows itself out */
  var helloTimer = null;

  function helloNode() {
    return document.querySelector('[data-vq-help-hello]');
  }

  function gateIsOpen() {
    var gate = document.querySelector('.voraqee-gate');
    return !!gate && !gate.hidden;
  }

  function hideHello() {
    var hello = helloNode();
    if (hello) hello.hidden = true;
    if (helloTimer) {
      window.clearTimeout(helloTimer);
      helloTimer = null;
    }
  }

  function showHello() {
    var hello = helloNode();
    if (!hello || !hello.hidden) return;

    hello.hidden = false;
    helloTimer = window.setTimeout(hideHello, HELLO_LIFE);
  }

  function startHello() {
    if (!helloNode()) return; /* not the home page */

    if (!gateIsOpen()) {
      showHello();
      return;
    }

    /* Escape closes the gate without navigating, so a single timer would miss
       that case. Re-check a bounded number of times instead. */
    var tries = 0;
    var poll = window.setInterval(function () {
      tries += 1;

      if (!gateIsOpen()) {
        window.clearInterval(poll);
        showHello();
        return;
      }

      if (tries >= 24) window.clearInterval(poll); /* ~12s, then let it go */
    }, 500);
  }

  window.setTimeout(startHello, HELLO_WAIT);

  document.addEventListener('click', function (event) {
    var t = event.target;
    if (!t.closest) return;

    if (t.closest('[data-vq-help-hello-close]')) {
      event.preventDefault();
      hideHello();
      return;
    }

    /* Tapping the wave opens the chat, but the launcher is named as the
       opener — focus returns there on close, and by then the bubble is gone. */
    if (t.closest('[data-vq-help-hello]')) {
      event.preventDefault();
      hideHello();
      openHelp(document.querySelector('[data-vq-help-open]'));
      return;
    }

    var opener = t.closest('[data-vq-help-open]');
    if (opener) {
      event.preventDefault();
      openHelp(opener);
      return;
    }

    if (t.closest('[data-vq-help-close]')) {
      event.preventDefault();
      closeHelp();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeHelp();
  });

  /* ======================================================================== */
  /* Toolbar panels                                                           */
  /* ------------------------------------------------------------------------ */
  /* A <details> only closes by pressing its own summary again, which is not
     what anyone expects of a panel that covers the page — especially the
     phone sheet, where the summary is behind the backdrop. Clicking away,
     pressing Escape, or using the close button all shut it.                  */
  /* ======================================================================== */

  function closeToolbarPanels(except) {
    document
      .querySelectorAll('.collection-grid__filterset[open], .collection-grid__sortset[open]')
      .forEach(function (panel) {
        if (panel !== except) panel.removeAttribute('open');
      });
  }

  document.addEventListener('click', function (event) {
    var t = event.target;
    if (!t.closest) return;

    if (t.closest('[data-vq-panel-close]')) {
      event.preventDefault();
      closeToolbarPanels();
      return;
    }

    /* Inside an open panel, or on the summary that opens it: leave it be. */
    var inside = t.closest('.collection-grid__filterset, .collection-grid__sortset');
    closeToolbarPanels(inside);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeToolbarPanels();
  });

  /* ======================================================================== */
  /* Card video on hover                                                      */
  /* ------------------------------------------------------------------------ */
  /* Pointing at a card plays its clip instead of cutting to a second still.
     Motion says more about a garment than another angle does.

     Only where a pointer can actually hover: on a touch screen there is no
     hover to end, so a clip started by a tap would run until the page
     changed. Those cards keep the still.                                     */
  /* ======================================================================== */

  register('card-video', '.showcase-card--video', function (card) {
    var video = card.querySelector('.showcase-card__video');
    if (!video) return;

    if (!window.matchMedia || !window.matchMedia('(hover: hover)').matches) {
      video.remove();
      return;
    }

    armVideo(video);
    video.autoplay = false;
    video.pause();

    function start() {
      card.classList.add('is-playing');
      tryPlay(video);
    }

    function stop() {
      card.classList.remove('is-playing');
      video.pause();
      /* Back to the first frame, so the next hover starts the clip rather
         than resuming someone else's. */
      try {
        video.currentTime = 0;
      } catch (e) {}
    }

    card.addEventListener('pointerenter', start);
    card.addEventListener('pointerleave', stop);
    /* Keyboard focus reaches the card through its title link. */
    card.addEventListener('focusin', start);
    card.addEventListener('focusout', function (event) {
      if (!card.contains(event.relatedTarget)) stop();
    });
  });

  /* ======================================================================== */
  /* Colour-filtered gallery                                                  */
  /* ------------------------------------------------------------------------ */
  /* Selecting a colour shows only that colour's frames.

     Shopify gives a variant exactly one featured image, so there is no list of
     per-variant media to read. What a merchant can control is alt text, so
     that is what this matches on: put the colour in the alt text of each
     photograph and the gallery follows the swatch.

     It fails OPEN — if nothing matches, every frame stays visible. A product
     whose alt text has not been written yet shows all its images rather than
     none, which is the difference between an unpolished gallery and an empty
     one.                                                                      */
  /* ======================================================================== */

  var COLOUR_KEYS = ['colour', 'color'];

  function colourFromVariant(variant, picker) {
    if (!variant || !picker) return '';

    /* Option order is the product's own, so the colour's position is found by
       reading the fieldset legends rather than assuming it is first. */
    var legends = picker.querySelectorAll('fieldset .variant-picker__label');
    for (var i = 0; i < legends.length; i++) {
      var name = (legends[i].textContent || '').trim().toLowerCase();
      for (var k = 0; k < COLOUR_KEYS.length; k++) {
        if (name.indexOf(COLOUR_KEYS[k]) !== -1) {
          return (variant.options[i] || '').trim().toLowerCase();
        }
      }
    }
    return '';
  }

  function filterGalleryByColour(colour) {
    var gallery = document.querySelector('.product-page .product-gallery');
    if (!gallery) return;

    var slides = Array.prototype.slice.call(
      gallery.querySelectorAll('.product-gallery__slide[data-vq-alt]')
    );
    if (!slides.length) return;

    var matched = [];
    if (colour) {
      /* A frame whose alt text opens with the colour is unambiguous. One that
         merely mentions it is a weaker signal — a product called "Black Skull
         Hoodie" puts "black" in every alt — so prefixes win and the looser
         test is only a fallback. */
      matched = slides.filter(function (slide) {
        return (slide.getAttribute('data-vq-alt') || '').indexOf(colour) === 0;
      });

      if (!matched.length) {
        matched = slides.filter(function (slide) {
          return (slide.getAttribute('data-vq-alt') || '').indexOf(colour) !== -1;
        });
      }
    }

    /* Nothing to go on, or nothing matched: show everything. */
    if (!matched.length) matched = slides;

    slides.forEach(function (slide) {
      slide.hidden = matched.indexOf(slide) === -1;
    });

    /* The cover column is whichever frame is now first. Leaving the marker on
       a hidden frame would empty that column. */
    slides.forEach(function (slide) {
      slide.removeAttribute('data-vq-cover');
    });
    matched[0].setAttribute('data-vq-cover', '');

    /* The cover spans one grid row per visible frame. Filtering changes how
       many there are, and a stale count would either cut the cover's travel
       short or stretch the column with empty rows. */
    gallery.style.setProperty('--vq-media-count', String(matched.length));

    /* The phone carousel's dots are rendered one per media item, so hiding
       slides left the full set behind: eight dots for four reachable frames,
       with the back half leading nowhere. Each dot carries the index of the
       slide it selects, so they are hidden in step with it. */
    var dots = gallery.querySelectorAll('.product-gallery__dot[data-index]');
    Array.prototype.forEach.call(dots, function (dot) {
      var slide = slides[parseInt(dot.getAttribute('data-index'), 10)];
      dot.hidden = !slide || slide.hidden;
    });

    /* The carousel's own counter reads this, so it must describe what is
       actually reachable rather than everything that was rendered. */
    gallery.setAttribute('data-count', String(matched.length));
  }

  document.addEventListener('variant:change', function (event) {
    var picker = document.querySelector('ds-variant-picker');
    filterGalleryByColour(colourFromVariant(event.detail && event.detail.variant, picker));
  });

  register('gallery-colour', 'ds-variant-picker', function (picker) {
    /* The page can load with a colour already chosen — from a ?variant= link,
       or the browser restoring a radio on back-navigation. */
    var checked = picker.querySelectorAll('fieldset input:checked');
    var legends = picker.querySelectorAll('fieldset .variant-picker__label');
    for (var i = 0; i < legends.length; i++) {
      var name = (legends[i].textContent || '').trim().toLowerCase();
      for (var k = 0; k < COLOUR_KEYS.length; k++) {
        if (name.indexOf(COLOUR_KEYS[k]) !== -1 && checked[i]) {
          filterGalleryByColour((checked[i].value || '').trim().toLowerCase());
          return;
        }
      }
    }
  });

  /* ======================================================================== */
  /* Buy now                                                                  */
  /* ------------------------------------------------------------------------ */
  /* A cart permalink — /cart/<variant>:1 — adds the variant and lands on
     checkout, so it needs no second form competing with the add-to-cart one
     beside it. The href has to track the variant picker, otherwise it would
     check out with whatever was selected when the page was rendered.         */
  /* ======================================================================== */

  function syncBuyNow(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-vq-buynow]').forEach(function (link) {
      var form = link.closest('.product-form') || document;
      var input = form.querySelector('[data-variant-id]');
      var base = link.getAttribute('data-vq-buynow-base') || '/cart';
      if (input && input.value) link.href = base + '/' + input.value + ':1';
    });
  }

  document.addEventListener('variant:change', function () {
    syncBuyNow(document);
  });

  register('buy-now', '[data-vq-buynow]', function () {
    syncBuyNow(document);
  });

  /* ======================================================================== */
  /* Saved items panel                                                        */
  /* ------------------------------------------------------------------------ */
  /* Opened from the header bookmark. Rows come straight out of storage, so it
     paints with no network and keeps working offline. Prices are emitted with
     data-vq-amount like every other price, which is what lets the currency
     converter reach them.                                                     */
  /* ======================================================================== */

  function formatBase(subunits) {
    var money = config().money || {};
    try {
      return new Intl.NumberFormat(money.locale || undefined, {
        style: 'currency',
        currency: money.baseCurrency || 'USD',
      }).format(subunits / 100);
    } catch (e) {
      return String(subunits / 100);
    }
  }

  function wishHost() {
    return document.getElementById('VoraqeeWishlist');
  }

  function renderWishlist() {
    var host = wishHost();
    if (!host) return;

    var list = host.querySelector('[data-vq-wish-list]');
    var empty = host.querySelector('[data-vq-wish-empty]');
    if (!list) return;

    var items = savedItems();
    var removeLabel = list.getAttribute('data-vq-remove-label') || 'Remove';

    list.innerHTML = '';
    if (empty) empty.hidden = items.length > 0;

    items.forEach(function (item) {
      var href = escapeHtml(item.url || '#');
      var row = document.createElement('li');
      row.className = 'vq-wish__item';

      var media = item.image
        ? '<a class="vq-wish__media" href="' + href + '" tabindex="-1" aria-hidden="true"><img src="' +
          escapeHtml(item.image) + '" alt="" loading="lazy"></a>'
        : '<span class="vq-wish__media"></span>';

      var price =
        item.price || item.price === 0
          ? '<span class="vq-money" data-vq-amount="' + escapeHtml(String(item.price)) + '">' +
            escapeHtml(formatBase(item.price)) + '</span>'
          : '';

      row.innerHTML =
        media +
        '<div class="vq-wish__info">' +
        '<a class="vq-wish__name" href="' + href + '">' + escapeHtml(item.title || '') + '</a>' +
        '<p class="vq-wish__price">' + price + '</p>' +
        '</div>' +
        '<button type="button" class="vq-wish__remove" data-vq-wish-remove="' + escapeHtml(item.id) + '">' +
        escapeHtml(removeLabel) + '</button>';

      list.appendChild(row);
    });

    /* The rows were just built, so they carry base prices until the converter
       is pointed at them. */
    paintAllPrices(list);
  }

  function openWishlist() {
    var host = wishHost();
    if (!host) return;
    renderWishlist();
    host.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    var panel = host.querySelector('.vq-wish__panel');
    if (panel) panel.focus();
  }

  function closeWishlist() {
    var host = wishHost();
    if (!host || host.hidden) return;
    host.hidden = true;
    document.documentElement.style.overflow = '';
  }

  document.addEventListener('click', function (event) {
    var t = event.target;
    if (!t.closest) return;

    if (t.closest('[data-vq-wish-open]')) {
      event.preventDefault();
      openWishlist();
      return;
    }

    if (t.closest('[data-vq-wish-close]')) {
      event.preventDefault();
      closeWishlist();
      return;
    }

    var remove = t.closest('[data-vq-wish-remove]');
    if (remove) {
      event.preventDefault();
      var id = remove.getAttribute('data-vq-wish-remove');
      writeSaved(
        savedItems().filter(function (entry) {
          return entry.id !== id;
        })
      );
      paintSaveControls();
      renderWishlist();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeWishlist();
  });

  /* ======================================================================== */
  /* Cart feedback                                                            */
  /* ======================================================================== */

  /* The theme's own ds-product-form already POSTs to /cart/add and opens the
     drawer. This does not reimplement any of that — it listens for the submit
     and then re-reads the cart to confirm a line actually landed, so the
     confirmation is never shown for a request that failed.

     Cart state is read from /cart.js rather than trusted from the form,
     because the same variant can be added from several surfaces and only the
     endpoint knows the settled result. */

  var lastCartCount = null;

  function cartUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/$/, '') + '/cart.js';
  }

  function refreshCart(announce) {
    return fetch(cartUrl(), { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('cart unavailable');
        return r.json();
      })
      .then(function (cart) {
        var count = typeof cart.item_count === 'number' ? cart.item_count : 0;

        /* Only announce a genuine addition. A removal or a quantity decrease
           reaches here too, and "Added to cart" would be a lie. */
        if (announce && lastCartCount !== null && count > lastCartCount) {
          toast(says('addedToCart', 'Added to cart'));
        }
        lastCartCount = count;
      })
      .catch(function () {});
  }

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
      /* The theme's handler runs on the same event and does the real work;
         this only follows up once it has had time to settle. */
      window.setTimeout(function () {
        refreshCart(true);
      }, 450);
    },
    true
  );

  /* ======================================================================== */
  /* Welcome back                                                             */
  /* ======================================================================== */

  /* sessionStorage, not localStorage: "welcome back" is about arriving, so it
     should return on the next visit. localStorage would fire once and then
     stay silent forever, which is the opposite of the intent. Per session
     rather than per page view, because a greeting that repeats on every
     navigation stops reading as a welcome. */

  function greet() {
    var customer = config().customer || {};
    if (!customer.loggedIn) return;

    try {
      if (sessionStorage.getItem('voraqee_greeted')) return;
      sessionStorage.setItem('voraqee_greeted', '1');
    } catch (e) {}

    var message = customer.firstName ? says('welcomeBack', '') : says('welcomeBackGeneric', '');
    if (message) toast(message);
  }

  /* ======================================================================== */
  /* Entry screen — country selection and hand-off                            */
  /* ======================================================================== */

  /* Extends the theme's own entry screen rather than adding a second one. It
     already owns the focus trap, the Escape handling and the "seen" memory, so
     this only adds the country step and the transition into the store.

     The sequence is: remember the country, grow the logo until it covers the
     screen, seal to black, then navigate. Navigating earlier would show the
     storefront painting in behind a half-finished animation, which is what
     makes a transition look broken.

     Driven by timers rather than transitionend: under reduced motion the
     transitions are removed and that event would never fire, stranding the
     visitor on the splash forever. A timer always runs out. */

  var ZOOM_DELAY = 40;
  var ZOOM_MS = 1150;
  var SEAL_MS = 420;

  register('entry-country', '[data-vq-country]', function (root) {
    var screen = root.closest('ds-entry-screen');
    if (!screen) return;

    var form = root.querySelector('form');
    var select = root.querySelector('[data-vq-country-select]');
    var enter = screen.querySelector('[data-vq-enter]');
    if (!enter) return;

    var leaving = false;

    /* How far the logo must grow to leave nothing of the splash visible.
       Measured rather than guessed: the mark sits above centre, so scaling by
       viewport ÷ mark alone would leave the bottom uncovered. */
    function coverScale(mark) {
      var r = mark.getBoundingClientRect();
      if (!r.width || !r.height) return 14;

      var cx = r.left + r.width / 2;
      var cy = r.top + r.height / 2;
      var reachX = Math.max(cx, window.innerWidth - cx) * 2;
      var reachY = Math.max(cy, window.innerHeight - cy) * 2;

      /* Overshoot so the edges are gone before the screen seals, and a cap so
         a small logo cannot ask for an absurd scale. */
      return Math.min(60, Math.max(reachX / r.width, reachY / r.height) * 1.15);
    }

    enter.addEventListener('click', function () {
      if (leaving) return;
      leaving = true;

      var code = select ? select.value : '';

      /* Remembered for the price converter, which needs a country even when
         Shopify's market reports the base currency for every one of them. */
      if (code && window.voraqeeSetCountry) window.voraqeeSetCountry(code);

      var mark = screen.querySelector('.entry-screen__logo');
      var reduced = reduceMotion.matches;

      if (mark && !reduced) {
        screen.style.setProperty('--vq-entry-zoom', coverScale(mark));
      }

      screen.classList.add('vq-entry-zooming');

      var zoomFor = reduced ? 0 : ZOOM_MS;

      window.setTimeout(function () {
        screen.classList.add('vq-entry-sealed');

        window.setTimeout(function () {
          /* A real country choice goes through Shopify's localization form so
             the market actually changes; the navigation it triggers is the
             hand-off. With no form to submit, the theme's own close() is used
             — it handles the "seen" memory, the focus release and the fade. */
          if (form && code && code !== (config().money || {}).countryCode) {
            form.submit();
            return;
          }

          if (typeof screen.close === 'function') screen.close();
          else screen.hidden = true;

          screen.classList.remove('vq-entry-zooming', 'vq-entry-sealed');
        }, reduced ? 80 : SEAL_MS);
      }, zoomFor + ZOOM_DELAY);
    });
  });

  /* ======================================================================== */
  /* Sticky purchase bar                                                      */
  /* ======================================================================== */

  /* The theme ships without one. It scrolls to the real controls rather than
     carrying a second add-to-cart form: two forms for one product is how
     variant state and cart behaviour drift apart, and the shopper still has to
     choose a size. */

  /* ======================================================================== */
  /* Reveal more products                                                     */
  /* ------------------------------------------------------------------------ */
  /* "View All" used to leave the page. The extra products are already in the
     markup, hidden, so the first press expands the grid in place.

     Once expanded there is nothing left to reveal, so the control becomes a
     real link to the collection rather than a button that no longer does
     anything — the way to the rest of the catalogue survives.                */
  /* ======================================================================== */

  register('reveal-more', '[data-vq-reveal]', function (button) {
    button.addEventListener('click', function () {
      var grid = button.closest('.product-showcase');
      if (!grid) return;

      var expanded = button.getAttribute('aria-expanded') === 'true';
      var extras = grid.querySelectorAll('[data-vq-extra]');

      Array.prototype.forEach.call(extras, function (item) {
        item.hidden = expanded;
      });

      button.setAttribute('aria-expanded', expanded ? 'false' : 'true');

      /* Phones show two, not three, so the collapsed state is a CSS concern as
         well as a `hidden` one. The attribute lets the stylesheet decide how
         many cards survive at each width. */
      if (expanded) grid.removeAttribute('data-vq-expanded');
      else grid.setAttribute('data-vq-expanded', 'true');

      var label = button.getAttribute(expanded ? 'data-vq-label-more' : 'data-vq-label-less');
      if (label) button.textContent = label;

      /* Collapsing can leave the button above the fold but the heading far
         up the page; bring the section back into view so the grid does not
         appear to jump somewhere else. */
      if (expanded) grid.scrollIntoView({ block: 'nearest' });
    });
  });

  register('sticky-bar', '[data-vq-sticky-bar]', function (bar) {
    var selector = bar.getAttribute('data-vq-sticky-after');
    var sentinel = selector ? document.querySelector(selector) : null;

    if (!sentinel || typeof IntersectionObserver !== 'function') {
      bar.setAttribute('data-vq-visible', 'true');
      return;
    }

    new IntersectionObserver(
      function (entries) {
        bar.setAttribute('data-vq-visible', entries[0].isIntersecting ? 'false' : 'true');
      },
      { threshold: 0 }
    ).observe(sentinel);
  });

  /* ======================================================================== */
  /* Wiring                                                                   */
  /* ======================================================================== */

  window.VoraqeeTheme = {
    register: register,
    run: run,
    toast: toast,
    get reducedMotion() {
      return reduceMotion.matches;
    },
  };

  function start() {
    run(document);
    paintSaveControls();
    applyFx();
    refreshCart(false);
    greet();
    paintRegionLabel();
    watchDocument();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  /* --------------------------------------------------------------------------
     Re-rendered markup
     The theme swaps DOM in place in two different ways, and only one of them
     announces itself.

     `shopify:section:load` fires in the theme editor. On a live storefront it
     does not: the cart drawer, the cart page and predictive search all replace
     their own markup after a fetch and say nothing. Prices in that new markup
     would still be server-rendered in the shop currency, so a shopper who
     changed a quantity would watch the cart snap back out of their currency.

     So we watch instead of listening. Both paths funnel into adopt(), which is
     safe to repeat: run() skips anything already carrying its name in
     data-vq-bound, and paintPrice() renders from the stashed base markup
     rather than from whatever is on screen.
     -------------------------------------------------------------------------- */

  function adopt(root) {
    run(root);
    paintSaveControls();
    paintAllPrices(root);
  }

  document.addEventListener('shopify:section:load', function (event) {
    adopt(event.target || document);
  });

  var pendingRoots = [];
  var flushQueued = false;

  function flushRoots() {
    flushQueued = false;
    var roots = pendingRoots;
    pendingRoots = [];
    roots.forEach(function (root) {
      /* A node can be added and removed again within one batch — a drawer
         rebuilt twice in quick succession. Adopting a detached tree would
         bind behaviours to markup nobody will ever see. */
      if (root.isConnected) adopt(root);
    });
  }

  function queueRoot(root) {
    pendingRoots.push(root);
    if (flushQueued) return;
    flushQueued = true;
    /* One pass per frame. A section swap arrives as many separate records. */
    if (window.requestAnimationFrame) window.requestAnimationFrame(flushRoots);
    else window.setTimeout(flushRoots, 16);
  }

  function watchDocument() {
    if (!window.MutationObserver || !document.body) return;

    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;

          /* Skip our own output, or painting a price would queue the spans it
             just wrote and paint them again every frame. Testing the parent
             rather than the node means a .vq-money element that arrives as a
             root is still adopted — closest() matches itself. */
          var parent = node.parentElement;
          if (parent && parent.closest('.vq-money, .vq-toast-host')) continue;

          queueRoot(node);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
